import { HotelDetailsViewModel, HotelPolicyViewModel, HotelRoomsAndRatesViewModel, PropertyInfoSubSectionViewModel } from 'models';

export interface ResultsCard {
  title: string;
  subTitle?: string;
  body: string[];
  paragraphView?: boolean;
}

export function getPropertyInfoForKeyword(
  keyword: string,
  hotel: HotelDetailsViewModel,
  offers: HotelRoomsAndRatesViewModel
): ResultsCard[] {
  const response = [];
  mapAdditionalInfo(hotel, keyword, response);
  mapPoliciesInfo(offers, hotel, keyword, response);

  return response;
}

function mapAdditionalInfo(hotel: HotelDetailsViewModel, keyword: string, response: any[]) {
  const additionalInfo = hotel && hotel.additionalInfo;
  const searchKeys = ['nearbyThingsSection', 'propertyAmenitiesSection', 'roomAmenitiesSection'];
  searchKeys.forEach(key => {
    if (additionalInfo && additionalInfo.hasOwnProperty(key)) {
      const sectionTitle = additionalInfo[key].title;
      const sections = additionalInfo[key].sections;
      const paragraphView = key === 'nearbyThingsSection' ? true : false;
      if (keywordPresent(keyword, sectionTitle)) {
        addSection(response, sections, sectionTitle, paragraphView);
      } else if (sections && sections.length > 0) {
        subsectionSearch(sections, keyword, response, sectionTitle, paragraphView);
      }
    }
  });
}

function subsectionSearch(sections: any, keyword: string, response: any[], sectionTitle: string, paragraphView: boolean) {
  sections.forEach(section => {
    const subSectionTitle = section.title;
    if (keywordPresent(keyword, subSectionTitle)) {
      addSubsection(response, sectionTitle, subSectionTitle, section, paragraphView);
    } else {
      searchAndAddText(response, section, sectionTitle, subSectionTitle, keyword, paragraphView);
    }
  });
}

function mapPoliciesInfo(offers: HotelRoomsAndRatesViewModel, hotel: HotelDetailsViewModel, keyword: string, response: any[]) {
  const policies = offers && offers.propertyInfo && offers.propertyInfo.policies;
  if (policies && policies.info) {
    mapChildBedPolicies(keyword, response, policies.info);
    mapCheckinCheckoutPolicies(keyword, response, policies.info);
    mapHouseRulesPolicies(keyword, response, policies.info);
    mapAccessMethodPolicies(keyword, response, policies.info);
  }

  const petPolicies = hotel && hotel.additionalInfo && hotel.additionalInfo.petsPolicy;
  if (petPolicies) {
    mapPetPolicies(keyword, response, petPolicies);
  }
}

function addSection(response: any[], sections: any, sectionTitle: string, paragraphView: boolean) {
  if (sections && sections.length > 0) {
    sections.forEach(section => {
      const subSectionTitle = section.title;
      addSubsection(response, sectionTitle, subSectionTitle, section, paragraphView);
    });
  }
}

function addSubsection(response: any[], title: string, subTitle: string, section: any, paragraphView: boolean) {
  const subSectionBody = section.body;
  if (subSectionBody && subSectionBody.length > 0) {
    response.push({
      title,
      subTitle,
      body: subSectionBody,
      paragraphView
    });
  }
}

function searchAndAddText(response: any[], section: any, title: string, subTitle: string, keyword: string, paragraphView: boolean) {
  const subSectionBody = section.body;
  if (subSectionBody && subSectionBody.length > 0) {
    const body = subSectionBody.filter(text => keywordPresent(keyword, text));
    if (body.length) {
      response.push({
        title,
        subTitle,
        body,
        paragraphView
      });
    }
  }
}

function keywordPresent(keyword: string, text: string) {
  if (!keyword || !text) {
    return false;
  }

  return text.toLowerCase().includes(keyword.toLowerCase());
}

function mapPetPolicies(keyword: string, response: any[], petPolicies?: PropertyInfoSubSectionViewModel) {
  const petPoliciesString =
    petPolicies && petPolicies.body && petPolicies.body.length > 0 && petPolicies.body[0] && petPolicies.body[0].length > 0;
  if (petPoliciesString) {
    const policies = getStringFromPoliciesHtml(petPolicies.body[0]);
    mapPoliciesArray(keyword, response, 'Policies', petPolicies.title ? petPolicies.title : '', policies);
  }
}

function mapChildBedPolicies(keyword: string, response: any[], info?: HotelPolicyViewModel) {
  const childBeds =
    info &&
    info.childBeds &&
    info.childBeds.lines &&
    info.childBeds.lines.length > 0 &&
    info.childBeds.lines[0] &&
    info.childBeds.lines[0].length > 0;
  if (childBeds) {
    const policies = getStringFromPoliciesHtml(info.childBeds.lines[0]);
    mapPoliciesArray(keyword, response, 'Policies', 'Children and extra beds', policies);
  }
}

function mapCheckinCheckoutPolicies(keyword: string, response: any[], info?: HotelPolicyViewModel) {
  const searchCheckin = info && info.checkinPolicies && info.checkinPolicies.lines && info.checkinPolicies.lines.length > 0;
  if (searchCheckin) {
    mapPoliciesArray(keyword, response, 'Policies', 'Check-in', info.checkinPolicies.lines);
  }
  const searchCheckout = info && info.checkoutPolicies && info.checkoutPolicies.lines && info.checkoutPolicies.lines.length > 0;
  if (searchCheckout) {
    mapPoliciesArray(keyword, response, 'Policies', 'Check-out', info.checkoutPolicies.lines);
  }
}

function mapHouseRulesPolicies(keyword: string, response: any[], info: HotelPolicyViewModel) {
  const searchHouseRules = info && info.houseRules && info.houseRules.title && info.houseRules.content;
  if (searchHouseRules) {
    mapPoliciesArray(keyword, response, 'Policies', info.houseRules.title, info.houseRules.content);
  }
}

function mapAccessMethodPolicies(keyword: string, response: any[], info?: HotelPolicyViewModel) {
  const searchAccessMethods =
    info &&
    info.accessMethodPolicies &&
    info.accessMethodPolicies.lines &&
    info.accessMethodPolicies.lines.length > 0 &&
    info.accessMethodPolicies.lines[0] &&
    info.accessMethodPolicies.lines[0].length > 0;
  if (searchAccessMethods) {
    mapPoliciesArray(keyword, response, 'Policies', 'Access methods', info.accessMethodPolicies.lines);
  }
}

function mapPoliciesArray(keyword: string, response: any[], title: string, subTitle: string, policies: string[]) {
  if (policies) {
    const body = policies.filter(text => keywordPresent(keyword, text));
    if (body.length) {
      response.push({
        title,
        subTitle,
        body
      });
    }
  }
}

// example data: <ul><li>policy 1</li></ul><ul><li>policy 2.</li></ul>
function getStringFromPoliciesHtml(stringWithEmbeddedHtml: string) {
  if (!stringWithEmbeddedHtml || typeof stringWithEmbeddedHtml !== 'string') {
    return;
  }
  const stringWithoutUl = stringWithEmbeddedHtml.replace(/\<ul\>|\<\/ul\>/g, '');
  const stringArrayNoHtml = stringWithoutUl.split(/\<li\>|\<\/li\>/);
  const liElements = stringArrayNoHtml.filter(item => item !== '');

  return liElements;
}
